import { useCallback, useReducer } from 'react'
import {
  type CreateChargeResponse as DiaCreateChargeResponse,
  CreateChargeRequest,
} from '@dialogue/silkroad'
import { useSelector } from 'APP/Hooks/redux'
import { selectAccessToken } from 'APP/Redux/LoginRedux'
import { selectPatientProfile } from 'APP/Redux/PatientRedux'
import Silkroad from 'APP/Services/Silkroad'
import { Camelized, camelizeKeys } from 'humps'

export interface CreateChargeOptions extends CreateChargeRequest {
  capture?: boolean
}

export type CreateChargeResponse = Camelized<DiaCreateChargeResponse>

export interface CreateChargeState {
  loading: boolean
  error: Error | null
  result: CreateChargeResponse | null
}

const initialCreateChargeState: CreateChargeState = {
  loading: false,
  error: null,
  result: null,
}

type CreateChargeAction =
  | { type: 'REQUEST' }
  | { type: 'SUCCESS'; payload: CreateChargeResponse }
  | { type: 'FAILURE'; payload: Error }
  | { type: 'RESET' }

export function createChargeReducer(
  state: CreateChargeState,
  action: CreateChargeAction
): CreateChargeState {
  switch (action.type) {
    case 'REQUEST':
      return {
        ...state,
        loading: true,
        error: null,
      }
    case 'SUCCESS':
      return {
        ...state,
        loading: false,
        result: action.payload,
        error: null,
      }
    case 'FAILURE':
      return {
        ...state,
        loading: false,
        error: action.payload,
        result: null,
      }
    case 'RESET':
      return initialCreateChargeState
    default:
      return state
  }
}

export const useCreateCharge = () => {
  const [state, dispatch] = useReducer(createChargeReducer, initialCreateChargeState)
  const accessToken = useSelector(selectAccessToken)
  const patientProfile = useSelector(selectPatientProfile)

  const createCharge = useCallback(
    async (options: CreateChargeOptions): Promise<CreateChargeResponse> => {
      if (!patientProfile?.id) {
        const error = new Error('Patient profile not available')

        dispatch({ type: 'FAILURE', payload: error })

        throw error
      }

      dispatch({ type: 'REQUEST' })

      try {
        const silkroad = Silkroad.create(accessToken)
        const result = await silkroad.createCharge(
          patientProfile.id.toString(),
          options.amount,
          options.description,
          options.meta || {},
          options.capture
        )

        const camelizedResult = camelizeKeys(result) as CreateChargeResponse

        dispatch({ type: 'SUCCESS', payload: camelizedResult })

        return camelizedResult
      } catch (error) {
        dispatch({ type: 'FAILURE', payload: error as Error })
        throw error
      }
    },
    [accessToken, patientProfile?.id]
  )

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return {
    createCharge,
    loading: state.loading,
    error: state.error,
    result: state.result,
    reset,
  }
}
